// src/Components/HoldingItems/HoldingItemsTable.jsx
import React, { useState, useMemo, useEffect } from 'react';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    IconButton,
    Checkbox,
    TextField,
    TableSortLabel,
    Box,
    Typography,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import editIconImage from '../../../assests/images/Icons/editIcon.svg';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import Constants from '../../../assests/Constants/constants';

/* ───────────────────────────────────────────── */

const columns = [
    { id: '_subType', labelKey: 'Type', type: 'string' },
    { id: 'creditAccount', labelKey: 'Client', type: 'string' },
    { id: 'receiptReference', labelKey: 'ReceiptNumber', type: 'string' },
    { id: 'paymentDate', labelKey: 'PaymentDate', type: 'date' },
    { id: 'originalAmount', labelKey: 'OriginalAmount', type: 'number' },
    { id: 'depositedSoFar', labelKey: 'DepositedSoFar', type: 'number' },
    { id: 'remainingAmount', labelKey: 'RemainingAmount', type: 'number' },
    { id: 'lastDepositDate', labelKey: 'LastDepositDate', type: 'date' },
    { id: 'lastDepositAmount', labelKey: 'LastDepositAmount', type: 'number' },
    { id: 'lastDepositReference', labelKey: 'DepositReference', type: 'string' },
    { id: 'description', labelKey: 'Description', type: 'string' },
    // { id: 'isDeposited', labelKey: 'Deposited', type: 'boolean' }
];

const headerStyle = {
    backgroundColor: '#cdd5ff',
    color: '#1222b3',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: 13,
    fontWeight: 700,
    height: 36,
    padding: '2px 8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
};

/* ───────────────────────────────────────────── */

const HoldingItemsTable = ({
    holdingItems,
    handleEdit,
    formatDateNew,
    paymentType,
    accountCardOptions,
    onSelectionChange = () => { }
}) => {


    const sharedInputProps = {
        disableUnderline: true,
        className: "custom-input-box-sizing-toggle",
        sx: {
            height: "48px",
            padding: "0 10px",
            borderRadius: "8px",
            background: "#F5F5F6",
            border: "1px solid transparent",
            "&:focus-within": {
                border: "2px solid var(--Foundation-Blue-Normal, #304FFF)",
            },
        },
    };

    const sharedInputStyle = dir => ({
        style: { textAlign: dir === "ltr" ? "left" : "right" },
    });

    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    /* ═══ 1.   flatten the tree → rows ════════════════════════════════════ */
    const flatRows = useMemo(() => {
        if (!holdingItems?.paymentAcceptances) return [];

        const rows = [];
        const add = (arr = [], subType, parentId) =>
            arr?.forEach(r => rows.push({ ...r, _subType: subType, paymentAcceptanceId: parentId }));

        const want = paymentType?.toString();
        const addAllByType = (getter, type) =>
            holdingItems.paymentAcceptances.forEach(pa => add(getter(pa), type, pa.id));

        if (want === Constants.HoldingItemType2.Check.toString()) addAllByType(pa => pa.checks, 'Check');
        else if (want === Constants.HoldingItemType2.Cash.toString()) addAllByType(pa => pa.cash, 'Cash');
        else if (want === Constants.HoldingItemType2.CreditCard.toString()) addAllByType(pa => pa.creditCards, 'CreditCard');
        else if (want === Constants.HoldingItemType2.BankTransfer.toString()) addAllByType(pa => pa.bankTransactions, 'BankTransfer');
        else { // “all”
            holdingItems.paymentAcceptances.forEach(pa => add(pa.checks, 'Check', pa.id));
        }
        return rows;
    }, [holdingItems, paymentType]);

    /* show extra bank-columns only when at least one row is a check */
    const showCheckCols = useMemo(
        () => flatRows.some(r => r._subType === 'Check'),
        [flatRows]
    );

    const totalAmount = useMemo(
        () => flatRows.reduce((sum, r) => sum + (Number.isFinite(r.remainingAmount) ? (r.remainingAmount || 0) : (r.amount || 0)), 0),
        [flatRows]
    );



    /* ═══ 2.   search filter ═════════════════════════════════════════════ */
    const [query, setQuery] = useState('');
    const searchLower = query.trim().toLowerCase();
    const searchedRows = useMemo(() => {
        if (!searchLower) return flatRows;
        return flatRows.filter(r =>
            [
                r._subType,
                r.creditAccount,
                r.debitAccount,
                r.description,
                r.amount?.toString(),
                r.isDeposited ? t('Yes') : t('No'),
                r.paymentDate ? formatDateNew(r.paymentDate) : ''
            ]
                .filter(Boolean)
                .some(field => field.toString().toLowerCase().includes(searchLower))
        );
    }, [flatRows, searchLower, formatDateNew, t]);

    /* ═══ 3.   sorting ═══════════════════════════════════════════════════ */
    const [sortCfg, setSortCfg] = useState({ key: 'paymentDate', dir: 'asc', type: 'date' });

    const sortedRows = useMemo(() => {
        const rows = [...searchedRows];
        const { key, dir, type } = sortCfg;

        const cmp = (a, b) => {
            const av = a[key] ?? '';
            const bv = b[key] ?? '';
            if (type === 'number') return av - bv;
            if (type === 'date') return new Date(av) - new Date(bv);
            if (type === 'boolean') return (av ? 1 : 0) - (bv ? 1 : 0);
            return av.toString().localeCompare(bv.toString(), undefined, { numeric: true });
        };
        rows.sort((a, b) => (dir === 'asc' ? cmp(a, b) : cmp(b, a)));
        return rows;
    }, [searchedRows, sortCfg]);

    const requestSort = (key, type) =>
        setSortCfg(prev =>
            prev.key === key ? { ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: 'asc', type });

    /* ═══ 4.   pagination ════════════════════════════════════════════════ */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(25);
    useEffect(() => setPage(0), [sortedRows]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);




    /* ═══ 5.   selection logic ═══════════════════════════════════════════ */
    const [selectedIds, setSelectedIds] = useState([]);
    const isSelected = id => selectedIds.includes(id);

    /* ═══ 5.1 expandable partial-deposit details ═════════════════════════ */
    const [expandedIds, setExpandedIds] = useState([]);
    const isExpanded = id => expandedIds.includes(id);
    const toggleExpand = id =>
        setExpandedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const toggleRow = id => {
        const newSel = isSelected(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
        setSelectedIds(newSel);
        onSelectionChange(newSel);
    };

    const toggleAll = checked => {
        const newSel = checked ? paginatedRows.map(r => r.id) : [];
        setSelectedIds(newSel);
        onSelectionChange(newSel);
    };

    const typeLabel = {
        0: t('CreditCards'),
        1: t('Checks'),
        2: t('BankTransfers'),
        3: t('Cashs')
    }[paymentType] || '';

    /* ═══ 6.   render ════════════════════════════════════════════════════ */
    return (
        <div className={direction} style={{ width: '100%', height: '65vh' }}>
            {/* Search box */}
            <Box sx={{ my: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <TextField
                    variant="outlined"
                    size="small"
                    dir={direction}
                    placeholder={t('Search')}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    sx={{
                        width: 280,
                        '& .MuiOutlinedInput-root': {
                            height: 40,
                            borderRadius: '12px',
                            backgroundColor: '#f5f6fd',
                            '& fieldset': { borderColor: '#e5e7f6', borderWidth: 2 },
                            '&:hover fieldset': { borderColor: '#c9d0ff' },
                            '&.Mui-focused fieldset': { borderColor: '#304FFF' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#304FFF', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: 40,
                        px: 2,
                        marginInlineStart: '16px',
                        borderRadius: '12px',
                        backgroundColor: '#f5f6fd',
                        border: '2px solid #e5e7f6',
                    }}
                >
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#666666' }}>
                        {t('Total')} {typeLabel} {t('InCashRegister')}:
                    </Typography>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#304FFF' }}>
                        ₪ {beautifyNumber(totalAmount)}
                    </Typography>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    height: '60vh',
                    overflow: 'auto',
                    borderRadius: '12px',
                    border: '1px solid #e5e7f6',
                    boxShadow: 'none',
                    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
                    scrollbarColor: '#d8daf2 transparent',
                    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#d8daf2',
                        borderRadius: 'var(--xtram-radius-pill, 999px)',
                    },
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {/* master checkbox */}
                            <TableCell align="center" sx={{ width: 50, ...headerStyle }}>
                                <Checkbox
                                    indeterminate={
                                        selectedIds.length > 0 && selectedIds.length < paginatedRows.length
                                    }
                                    checked={
                                        paginatedRows.length > 0 && selectedIds.length === paginatedRows.length
                                    }
                                    onChange={e => toggleAll(e.target.checked)}
                                />
                            </TableCell>

                            {/* edit‑icon column (non‑sortable) */}
                            <TableCell sx={headerStyle} />

                            {/* expand column */}
                            <TableCell sx={headerStyle} />

                            {/* dynamic sortable columns */}
                            {columns.map(col => (
                                <TableCell key={col.id} align="center" sx={headerStyle}>
                                    <TableSortLabel
                                        active={sortCfg.key === col.id}
                                        direction={sortCfg.key === col.id ? sortCfg.dir : 'asc'}
                                        onClick={() => requestSort(col.id, col.type)}
                                    >
                                        {t(col.labelKey)}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            {showCheckCols && (
                                <>
                                    <TableCell align="center" sx={headerStyle}>{t('BankName')}</TableCell>
                                    <TableCell align="center" sx={headerStyle}>{t('BankCode')}</TableCell>
                                    <TableCell align="center" sx={headerStyle}>{t('BankBranchCode')}</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedRows.map((row, idx) => (
                            <React.Fragment key={row.id}>
                                <TableRow
                                    hover
                                    sx={{
                                        '& td': { padding: '2px 4px', fontSize: 13 },
                                        '& .MuiIconButton-root': { padding: '3px' },
                                        '& .MuiCheckbox-root': { padding: '3px' },
                                    }}
                                >
                                {/* row checkbox */}
                                <TableCell align="center">
                                    <Checkbox
                                        checked={isSelected(row.id)}
                                        onChange={() => toggleRow(row.id)}
                                    />
                                </TableCell>

                                {/* edit button */}
                                <TableCell align="center" sx={{ width: 40 }}>
                                    <IconButton onClick={() => handleEdit(row, idx)}>
                                        <img src={editIconImage} alt="edit" />
                                    </IconButton>
                                </TableCell>

                                {/* expand partial deposits */}
                                <TableCell align="center" sx={{ width: 40 }}>
                                    {Array.isArray(row.partialDeposits) && row.partialDeposits.length > 0 ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleExpand(row.id)}
                                            aria-label={isExpanded(row.id) ? t('Collapse') : t('Expand')}
                                        >
                                            {isExpanded(row.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    ) : (
                                        <span />
                                    )}
                                </TableCell>

                                <TableCell align="center">{t(row._subType)}</TableCell>

                                <TableCell align="center">
                                    {accountCardOptions?.find(option => option.cardId === row.creditAccount)?.name || '-'}
                                </TableCell>

                                <TableCell align="center">{row.receiptReference || '-'}</TableCell>

                                <TableCell align="center">
                                    {row.paymentDate ? formatDateNew(row.paymentDate) : '-'}
                                </TableCell>
                                <TableCell align="center">
                                    {row.originalAmount !== undefined ? beautifyNumber(row.originalAmount) : beautifyNumber(row.amount || 0)}
                                </TableCell>

                                <TableCell align="center">
                                    {row.depositedSoFar !== undefined ? beautifyNumber(row.depositedSoFar || 0) : beautifyNumber(0)}
                                </TableCell>

                                <TableCell align="center">
                                    {row.remainingAmount !== undefined ? beautifyNumber(row.remainingAmount || 0) : beautifyNumber(row.amount || 0)}
                                </TableCell>

                                <TableCell align="center">
                                    {row.lastDepositDate ? formatDateNew(row.lastDepositDate) : '-'}
                                </TableCell>

                                <TableCell align="center">
                                    {row.lastDepositAmount !== undefined && row.lastDepositAmount !== null
                                        ? beautifyNumber(row.lastDepositAmount)
                                        : '-'}
                                </TableCell>

                                <TableCell align="center">{row.lastDepositReference || row.depositReference || '-'}</TableCell>

                                <TableCell align="center">{row.description || '-'}</TableCell>
                                {/* <TableCell align="center">{row.isDeposited ? t('Yes') : t('No')}</TableCell> */}
                                {row._subType === 'Check' && (
                                    <>
                                        <TableCell align="center">{row.bankName || '-'}</TableCell>
                                        <TableCell align="center">{row.bankCode || '-'}</TableCell>
                                        <TableCell align="center">{row.bankBranchCode || '-'}</TableCell>
                                    </>
                                )}
                                </TableRow>

                                {isExpanded(row.id) && Array.isArray(row.partialDeposits) && row.partialDeposits.length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2 + 1 + columns.length + (showCheckCols ? 3 : 0)} sx={{ background: '#FAFAFB' }}>
                                            <Box sx={{ px: 2, py: 1 }}>
                                                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                                                    {t('PartialDeposits')}
                                                </Typography>

                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700 }}>{t('PaymentDate')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{t('Amount')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{t('DepositReference')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{t('UploadNumber')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{t('Reference')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {row.partialDeposits.map(pd => (
                                                            <TableRow key={pd.id}>
                                                                <TableCell>{pd.depositDate ? formatDateNew(pd.depositDate) : '-'}</TableCell>
                                                                <TableCell>₪ {beautifyNumber(pd.partialAmount || 0)}</TableCell>
                                                                <TableCell>{pd.depositReference || '-'}</TableCell>
                                                                <TableCell>{pd.journalEntryUploadNumber ?? '-'}</TableCell>
                                                                <TableCell>{pd.journalEntryReference || '-'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* pagination */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => {
                    setRpp(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('Rows per page')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
                }
            />
        </div>
    );
};

export default HoldingItemsTable;
